<template>
  <Teleport to="body">
    <div v-if="visible" class="fixed inset-0 z-50 overflow-y-auto">
      <!-- 遮罩层 -->
      <div class="fixed inset-0 bg-slate-900/55 transition-opacity" @click="handleClose"></div>

      <!-- 弹窗主体 -->
      <div class="flex min-h-full items-center justify-center p-4">
        <div class="relative w-full max-w-xl overflow-hidden rounded-xl bg-white shadow-2xl ring-1 ring-slate-900/10 transform transition-all">
          <!-- 头部 -->
          <div class="flex items-start justify-between gap-4 border-b border-slate-200 bg-slate-50 px-6 py-5">
            <div class="flex min-w-0 items-center gap-3">
              <div class="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600 ring-1 ring-primary-100">
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 18.72a9.094 9.094 0 003.75 1.03 1.5 1.5 0 001.5-1.5v-.621a4.5 4.5 0 00-2.548-4.055l-.253-.126a.75.75 0 00-.828.111l-1.253 1.253a.75.75 0 01-.91.113 7.5 7.5 0 01-3.383-3.383.75.75 0 01.113-.91l1.253-1.253a.75.75 0 00.111-.828l-.126-.253A4.5 4.5 0 0011.371 5.75h-.621a1.5 1.5 0 00-1.5 1.5 9.094 9.094 0 001.03 3.75"/>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19.128A9.38 9.38 0 0112 19.5a9.5 9.5 0 119.5-9.5c0 1.02-.16 2.003-.456 2.925"/>
                </svg>
              </div>
              <div class="min-w-0">
                <h3 class="truncate text-lg font-semibold leading-6 text-slate-900">
                  {{ referrerData?.id ? '编辑引入人' : '新增引入人' }}
                </h3>
                <div v-if="referrerData?.id" class="mt-1 text-sm text-slate-500">
                  ID {{ referrerData.id }}
                </div>
              </div>
            </div>
            <button
              @click="handleClose"
              type="button"
              class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-white hover:text-slate-700"
              title="关闭"
            >
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <!-- 表单内容 -->
          <div class="px-6 py-5">
            <el-form
              ref="formRef"
              :model="formData"
              :rules="rules"
              label-width="100px"
              class="space-y-1"
            >
              <el-form-item label="姓名" prop="name">
                <input
                  v-model="formData.name"
                  type="text"
                  placeholder="请输入引入人姓名"
                  class="form-input w-full"
                >
              </el-form-item>

              <el-form-item label="联系电话" prop="phone">
                <input
                  v-model="formData.phone"
                  type="text"
                  placeholder="请输入联系电话"
                  class="form-input w-full"
                >
              </el-form-item>

              <el-form-item label="微信号" prop="wechat">
                <input
                  v-model="formData.wechat"
                  type="text"
                  placeholder="请输入微信号"
                  class="form-input w-full"
                >
              </el-form-item>

              <el-form-item label="引入类型" prop="referrerType">
                <select
                  v-model="formData.referrerType"
                  class="form-select-native w-full"
                >
                  <option value="">请选择引入类型</option>
                  <option value="0">渠道</option>
                  <option value="1">客户转介绍</option>
                  <option value="2">政府</option>
                  <option value="3">其他</option>
                </select>
              </el-form-item>

              <el-form-item label="备注">
                <textarea
                  v-model="formData.remark"
                  rows="3"
                  placeholder="请输入备注（选填）"
                  class="form-textarea"
                ></textarea>
              </el-form-item>
            </el-form>
          </div>

          <!-- 底部按钮 -->
          <div class="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4 sm:flex-row sm:items-center sm:justify-end">
            <button
              @click="handleClose"
              type="button"
              class="btn-secondary w-full sm:w-auto"
            >
              取消
            </button>
            <button
              @click="handleSubmit"
              :disabled="submitting"
              type="button"
              class="btn-primary w-full sm:w-auto"
            >
              <span v-if="submitting">保存中...</span>
              <span v-else>保存</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, reactive, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { createReferrer, updateReferrer } from '@/api/referrer'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  referrerData: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['update:visible', 'success'])

const formRef = ref(null)
const submitting = ref(false)

// 表单数据
const formData = reactive({
  name: '',
  phone: '',
  wechat: '',
  referrerType: '',
  remark: ''
})

// 自定义验证器：手机号格式
const validatePhone = (rule, value, callback) => {
  if (value && !/^1[3-9]\d{9}$|^0\d{2,3}-?\d{7,8}$/.test(value)) {
    callback(new Error('请输入正确的手机号码或电话号码'))
  } else {
    callback()
  }
}

// 表单验证规则
const rules = {
  name: [
    { required: true, message: '请输入引入人姓名', trigger: 'blur' },
    { min: 1, max: 50, message: '姓名长度不能超过50个字符', trigger: 'blur' }
  ],
  phone: [
    { validator: validatePhone, trigger: 'blur' }
  ],
  wechat: [
    { required: true, message: '请输入微信号', trigger: 'blur' }
  ],
  referrerType: [
    { required: true, message: '请选择引入类型', trigger: 'change' }
  ]
}

// 监听弹窗显示
watch(() => props.visible, (val) => {
  if (val && props.referrerData) {
    // 编辑模式
    formData.id = props.referrerData.id
    formData.name = props.referrerData.name || ''
    formData.phone = props.referrerData.phone || ''
    formData.wechat = props.referrerData.wechat || ''
    formData.referrerType = props.referrerData.referrerType || ''
    formData.remark = props.referrerData.remark || ''
  } else if (val) {
    // 新增模式
    formData.id = null
    formData.name = ''
    formData.phone = ''
    formData.wechat = ''
    formData.referrerType = ''
    formData.remark = ''
  }
})

// 关闭弹窗
const handleClose = () => {
  emit('update:visible', false)
}

// 提交表单
const handleSubmit = async () => {
  try {
    await formRef.value.validate()
    submitting.value = true

    if (formData.id) {
      // 编辑
      const updatedReferrer = await updateReferrer({
        id: formData.id,
        name: formData.name,
        phone: formData.phone,
        wechat: formData.wechat,
        referrerType: formData.referrerType,
        remark: formData.remark
      })
      ElMessage.success('更新成功')
      emit('success', {
        action: 'update',
        referrer: updatedReferrer
      })
    } else {
      // 新增
      const createdReferrer = await createReferrer({
        name: formData.name,
        phone: formData.phone,
        wechat: formData.wechat,
        referrerType: formData.referrerType,
        remark: formData.remark
      })
      ElMessage.success('创建成功')
      emit('success', {
        action: 'create',
        referrer: createdReferrer
      })
    }

    handleClose()
  } catch (error) {
    console.error('保存引入人失败:', error)
    if (error !== false) {
      ElMessage.error(error.message || '保存失败')
    }
  } finally {
    submitting.value = false
  }
}
</script>
